import "./resultSeriesStyle.scss";
import { useMantineColorScheme } from "@mantine/core";
import React, { useCallback, useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import moment from "moment";
import FullscreenModal from "../common/FullscreenModal/FullscreenModal";
import { useTranslation } from "react-i18next";
import { formatEnUsValue } from "../../utils/helpers";

// Secure unique ID generator
let __uidCounter = 0;
function secureUniqueId(prefix = "uid") {
  try {
    if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return `${prefix}-${hex}`;
    }
  } catch (e) { /* ignore */ }
  const ts = Date.now().toString(36);
  __uidCounter = (__uidCounter + 1) % 1_000_000;
  return `${prefix}-${ts}-${__uidCounter.toString(36)}`;
}

// Two fixed thermal series
const THERMAL_SERIES_CONFIG = [
  { id: "thermalBefore", key: "thermalBefore", color: "#ff7f50", label: "Energy Required" },
  { id: "thermalAfter",  key: "thermalAfter",  color: "#ff66aa", label: "Energy Served"  },
];

const fontFamily = "Nunito, sans-serif";

const ResultSeriesThermal = ({
  results,
  onFullscreen,
  kpiProjectSettings,
  inModalInstance = false,
  legendGap = 16,
}) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const wrapperRef = useRef(null);
  const mainRef = useRef(null);
  const tooltipRef = useRef(null);
  const [hidden, setHidden] = useState(new Set());
  const [xDomain, setXDomain] = useState(null);
  const [yMode, setYMode] = useState("full");
  const [panMode, setPanMode] = useState(false);
  const [powerUnit, setPowerUnit] = useState("W");
  const [fullscreenModal, setFullscreenModal] = useState(false);

  const unitHelpers = React.useMemo(() => {
    const unitFactor = powerUnit === "kW" ? 1 / 1000 : 1;
    const unitLabel = powerUnit;
    const unitLabelHtml = powerUnit === "kW" ? "kW<sub><i>th</i></sub>" : "W<sub><i>th</i></sub>";
    const scaleValue = (v) => { const num = +v; return Number.isFinite(num) ? num * unitFactor : null; };
    const formatValue = (v) => {
      if (v == null || !Number.isFinite(v)) return "-";
      const hasDecimal = Math.abs(v % 1) > 1e-6;
      return hasDecimal ? (Math.round(v * 1000) / 1000).toString() : v.toString();
    };
    const powerAxisLabel = powerUnit === "kW" ? t("timeSeries.axis.powerKW") : t("timeSeries.axis.powerW");
    return { unitFactor, unitLabel, unitLabelHtml, scaleValue, formatValue, powerAxisLabel };
  }, [powerUnit, t]);

  const handleCloseModal = useCallback(() => {
    setFullscreenModal(false);
    if (typeof onFullscreen === "function") onFullscreen();
  }, [onFullscreen]);

  // Build series from thermalBefore / thermalAfter
  const series = React.useMemo(() => {
    const r = results || {};
    return THERMAL_SERIES_CONFIG.reduce((acc, cfg) => {
      const matchKey = Object.keys(r).find((k) => k.toLowerCase() === cfg.key.toLowerCase());
      const raw = matchKey ? r[matchKey] : null;
      if (Array.isArray(raw) && raw.length) {
        acc.push({ key: cfg.id, sourceKey: matchKey, label: cfg.label, color: cfg.color, values: raw });
      }
      return acc;
    }, []);
  }, [results]);

  const maxLen = series.length ? Math.max(...series.map((s) => s.values.length)) : 0;
  const stepMs = 15 * 60 * 1000;
  const startDate = useRef(null);
  if (maxLen && !startDate.current) {
    if (kpiProjectSettings?.startYear) {
      startDate.current = new Date(kpiProjectSettings.startYear, 0, 1, 0, 0, 0, 0);
    } else {
      const d = new Date(); d.setFullYear(d.getFullYear() - 1); d.setHours(0, 0, 0, 0);
      startDate.current = d;
    }
  }
  const times = React.useMemo(() => {
    if (!maxLen || !startDate.current) return [];
    const base = startDate.current.getTime();
    const arr = new Array(maxLen);
    for (let i = 0; i < maxLen; i++) arr[i] = new Date(base + i * stepMs);
    return arr;
  }, [maxLen]);

  const sampleIndices = (startIdx, endIdx, maxPoints) => {
    const span = endIdx - startIdx + 1;
    if (span <= maxPoints) return d3.range(startIdx, endIdx + 1);
    const step = Math.ceil(span / maxPoints);
    const arr = [];
    for (let i = startIdx; i <= endIdx; i += step) arr.push(i);
    if (arr[arr.length - 1] !== endIdx) arr.push(endIdx);
    return arr;
  };

  const render = () => {
    const root = wrapperRef.current;
    if (!root) return;
    const { unitLabel, unitLabelHtml, scaleValue, powerAxisLabel } = unitHelpers;
    const mainSvg = d3.select(mainRef.current);
    mainSvg.selectAll("*").remove();
    if (!maxLen) {
      mainSvg.append("text").attr("x", 20).attr("y", 30).text(t("common.noDataAvailable")).attr("fill", colorScheme === "dark" ? "#d5d7e0" : "#495057");
      return;
    }

    const chartScale = 1.18;
    const width = root.clientWidth;
    const fullExtent = d3.extent(times);
    const fullX = d3.scaleTime().domain(fullExtent).range([60, width - 20]);

    let domainStart = xDomain ? xDomain[0] : times[0];
    let domainEnd = xDomain ? xDomain[1] : times[maxLen - 1];
    if (domainStart < times[0]) domainStart = times[0];
    if (domainEnd > times[maxLen - 1]) domainEnd = times[maxLen - 1];
    const originalDomainStart = domainStart;
    const originalDomainEnd = domainEnd;
    const step = times.length > 1 ? times[1] - times[0] : stepMs;
    domainStart = new Date(domainStart.getTime() - step / 2);
    domainEnd = new Date(domainEnd.getTime() + step / 2);
    const currentDomain = [domainStart, domainEnd];

    const mainH = 470 * chartScale;
    const margin = { left: 60 * chartScale, right: 20 * chartScale, top: legendGap * chartScale, bottom: 60 * chartScale };
    mainSvg.attr("width", width).attr("height", 550 * chartScale).style("background", colorScheme === "dark" ? "#111111" : "#ffffff");
    const viewX = fullX.copy().domain(currentDomain);

    let startIdx = d3.bisector((d) => d).left(times, currentDomain[0]);
    let endIdx = d3.bisector((d) => d).right(times, currentDomain[1]) - 1;
    startIdx = Math.max(0, startIdx - 1);
    endIdx = Math.min(times.length - 1, endIdx + 1);

    const activeSeries = series.filter((s) => !hidden.has(s.key));

    // Y domain
    let globalMax = Number.NEGATIVE_INFINITY; let globalMin = Number.POSITIVE_INFINITY;
    for (const s of activeSeries) { for (let i = 0; i < (s.values || []).length; i++) { const num = scaleValue(s.values[i]); if (!Number.isFinite(num)) continue; if (num > globalMax) globalMax = num; if (num < globalMin) globalMin = num; } }
    if (globalMax === Number.NEGATIVE_INFINITY) { globalMax = 1; globalMin = 0; }
    if (globalMin === Number.POSITIVE_INFINITY) globalMin = 0;
    let maxValWindow = Number.NEGATIVE_INFINITY; let minValWindow = Number.POSITIVE_INFINITY;
    for (const s of activeSeries) { const vals = s.values || []; for (let i = startIdx; i <= endIdx && i < vals.length; i++) { const num = scaleValue(vals[i]); if (!Number.isFinite(num)) continue; if (num > maxValWindow) maxValWindow = num; if (num < minValWindow) minValWindow = num; } }
    if (maxValWindow === Number.NEGATIVE_INFINITY) maxValWindow = 1;
    if (minValWindow === Number.POSITIVE_INFINITY) minValWindow = 0;
    let useMax = yMode === "full" ? globalMax : maxValWindow;
    let useMin = yMode === "full" ? globalMin : minValWindow;
    if (useMin > 0) useMin = 0;   // anchor baseline at 0 for positive-only data
    if (useMax < 0) useMax = 0;   // anchor baseline at 0 for negative-only data
    if (useMax === useMin) { if (useMax === 0) { useMax = 1; } else { useMin = 0; } }
    let yTop = useMax; let yBottomBase = useMin;
    if (yTop === yBottomBase) { if (yTop === 0) { yTop = 1; } else { yBottomBase = 0; } }
    yTop += (yTop - yBottomBase) * 0.002;
    const viewY = d3.scaleLinear().domain([yBottomBase, yTop]).range([mainH - margin.bottom, margin.top]);
    const niceDomain = viewY.domain();

    const idxs = sampleIndices(startIdx, endIdx, 4000);
    const spanMs = currentDomain[1] - currentDomain[0];
    const dayMs = 24 * 60 * 60 * 1000;
    const plotW = width - 80;
    const maxTicks = Math.max(4, Math.min(14, Math.floor(plotW / 80)));
    let xAxis = d3.axisBottom(viewX);

    // Minimap
    const minimapHeight = 48; const minimapGap = 8; const minimapMargin = { left: 60, right: 20 };
    const minimapWidth = width; const minimapY = mainH + minimapGap;
    const minimapSvg = mainSvg.append("g").attr("class", "minimap").attr("transform", `translate(0,${minimapY})`);
    minimapSvg.append("rect").attr("x", minimapMargin.left).attr("y", 0).attr("width", minimapWidth - minimapMargin.left - minimapMargin.right).attr("height", minimapHeight).attr("fill", colorScheme === "dark" ? "#1a1a1a" : "#f5f5f5");
    const miniX = d3.scaleTime().domain([times[0], times[times.length - 1]]).range([minimapMargin.left, minimapWidth - minimapMargin.right]);
    let miniYMin = Number.POSITIVE_INFINITY; let miniYMax = Number.NEGATIVE_INFINITY;
    series.forEach((s) => { (s.values || []).forEach((v) => { const num = +v; if (!Number.isFinite(num)) return; if (num < miniYMin) miniYMin = num; if (num > miniYMax) miniYMax = num; }); });
    if (!Number.isFinite(miniYMin)) miniYMin = 0; if (!Number.isFinite(miniYMax)) miniYMax = 1;
    if (miniYMin > 0) miniYMin = 0; if (miniYMax < 0) miniYMax = 0; if (miniYMax === miniYMin) miniYMax = miniYMin + 1;
    const miniY = d3.scaleLinear().domain([miniYMin, miniYMax]).range([minimapHeight - 8, 8]);
    const minimapChartG = minimapSvg.append("g").attr("class", "minimap-chart-lines");
    if (activeSeries.length && times.length) {
      activeSeries.forEach((s) => {
        const points = [];
        for (let i = 0; i < s.values.length && i < times.length; i++) { const v = +s.values[i]; if (!Number.isFinite(v)) continue; points.push([miniX(times[i]), miniY(v)]); }
        if (points.length > 1) { const lg = d3.line().x((d) => d[0]).y((d) => d[1]); minimapChartG.append("path").attr("fill", "none").attr("stroke", s.color).attr("stroke-width", 1.2).attr("opacity", 0.95).attr("d", lg(points)); }
      });
    }
    let ignoreBrushEvent = false;
    const brush = d3.brushX().extent([[minimapMargin.left, 0], [minimapWidth - minimapMargin.right, minimapHeight]])
      .on("brush end", function (event) {
        if (ignoreBrushEvent || !event.selection) return;
        const [x0, x1] = event.selection;
        if (event.type === "brush" || event.type === "end") setXDomain([miniX.invert(x0), miniX.invert(x1)]);
      });
    const brushG = minimapSvg.append("g").attr("class", "minimap-brush").call(brush);
    brushG.selectAll("rect.overlay").style("pointer-events", "none");
    const desired = xDomain ? [miniX(xDomain[0]), miniX(xDomain[1])] : [miniX(times[0]), miniX(times[times.length - 1])];
    const currentSel = d3.brushSelection(brushG.node());
    if (!currentSel || currentSel[0] !== desired[0] || currentSel[1] !== desired[1]) { ignoreBrushEvent = true; brushG.call(brush.move, desired); ignoreBrushEvent = false; }
    if (xDomain) {
      const selX0 = miniX(xDomain[0]); const selX1 = miniX(xDomain[1]);
      const selRect = minimapSvg.append("rect").attr("x", selX0).attr("y", 0).attr("width", Math.max(1, selX1 - selX0)).attr("height", minimapHeight)
        .attr("fill", colorScheme === "dark" ? "rgba(80,160,255,0.18)" : "rgba(0,123,255,0.13)").attr("stroke", colorScheme === "dark" ? "#4da3ff" : "#007bff").attr("stroke-width", 1.2).style("cursor", "grab");
      selRect.on("pointerdown", function (ev) {
        ev.preventDefault(); if (ev.button !== 0) return;
        const startX = ev.clientX; const startDomain = [...xDomain]; const domainSpanMs = startDomain[1] - startDomain[0]; const msPerPixel = domainSpanMs / Math.max(1, selX1 - selX0);
        const onMove = (e) => { const dx = e.clientX - startX; let shiftMs = dx * msPerPixel; let ns = new Date(startDomain[0].getTime() + shiftMs); let ne = new Date(startDomain[1].getTime() + shiftMs); if (ns < times[0]) { ns = times[0]; ne = new Date(ns.getTime() + domainSpanMs); } if (ne > times[times.length - 1]) { ne = times[times.length - 1]; ns = new Date(ne.getTime() - domainSpanMs); } setXDomain([ns, ne]); };
        const onUp = () => { window.removeEventListener("pointermove", onMove, true); window.removeEventListener("pointerup", onUp, true); selRect.style("cursor", "grab"); };
        selRect.style("cursor", "grabbing");
        window.addEventListener("pointermove", onMove, { passive: true, capture: true });
        window.addEventListener("pointerup", onUp, { passive: true, capture: true });
      });
    }

    // X axis tick granularity
    if (spanMs <= 16 * dayMs) {
      const intervalsInView = spanMs / stepMs; let stride = Math.ceil(intervalsInView / maxTicks);
      const friendly = [1, 2, 4, 8, 12, 16, 24, 32, 48, 64, 96]; stride = friendly.find((f) => f >= stride) || stride;
      const minutes = stride * 15;
      if (minutes < 24 * 60) { xAxis = xAxis.ticks(d3.timeMinute.every(minutes)); } else { xAxis = xAxis.ticks(d3.timeDay.every(1)); }
      xAxis.tickFormat(d3.timeFormat("%-d %b %Y %-H:%M"));
    } else if (spanMs <= 120 * dayMs) { xAxis = xAxis.ticks(d3.timeWeek.every(1)).tickFormat(d3.timeFormat("%-d %b %Y")); }
    else { xAxis = xAxis.ticks(d3.timeMonth.every(1)).tickFormat(d3.timeFormat("%b %Y")); }

    const maxForFormat = Math.max(Math.abs(niceDomain[0]), Math.abs(niceDomain[1]));
    const fmtThousands = d3.format(","); const fmtShort = d3.format(".2s");
    const yAxis = d3.axisLeft(viewY).ticks(5).tickFormat((d) => { const abs = Math.abs(d); if (maxForFormat >= 1e9) return fmtShort(d).replace("G", "B"); if (maxForFormat >= 1e6) return fmtShort(d); return fmtThousands(d); });

    // Legend
    const legendContainer = root.querySelector(".rs-legend");
    if (legendContainer) {
      legendContainer.innerHTML = "";
      const rawValue = (value) => value == null || !Number.isFinite(value) ? "-" : formatEnUsValue(value);
      series.forEach((s) => {
        let wMin = Infinity; let wMax = -Infinity;
        for (let i = startIdx; i <= endIdx && i < s.values.length; i++) { const num = scaleValue(s.values[i]); if (!Number.isFinite(num)) continue; if (num < wMin) wMin = num; if (num > wMax) wMax = num; }
        if (wMin === Infinity) { wMin = null; wMax = null; }
        const btn = document.createElement("button"); btn.type = "button";
        btn.style.cssText = "display:flex;align-items:center;gap:6px;margin:2px 10px;padding:4px 10px;border:1px solid #ccc;border-radius:4px;cursor:pointer;";
        btn.style.background = hidden.has(s.key) ? (colorScheme === "dark" ? "#1e1e1e" : "#f1f3f5") : (colorScheme === "dark" ? "#2e2e2e" : "#ffffff");
        btn.style.opacity = hidden.has(s.key) ? "0.45" : "1"; btn.title = hidden.has(s.key) ? `Show ${s.label}` : `Hide ${s.label}`;
        const swatch = document.createElement("span"); swatch.style.cssText = `width:14px;height:8px;border-radius:2px;background:${s.color};flex-shrink:0;`;
        const lbl = document.createElement("span"); lbl.style.cssText = `font-size:11px;font-family:${fontFamily};color:${colorScheme === "dark" ? "#d5d7e0" : "#222"};`;
        lbl.innerHTML = `${s.label}: Min ${rawValue(wMin)} ${unitLabelHtml} , Max ${rawValue(wMax)} ${unitLabelHtml}`;
        btn.appendChild(swatch); btn.appendChild(lbl);
        btn.addEventListener("click", () => setHidden((prev) => { const next = new Set(prev); if (next.has(s.key)) next.delete(s.key); else next.add(s.key); return next; }));
        legendContainer.appendChild(btn);
      });
    }

    const axisOffset = 16;
    const xAxisG = mainSvg.append("g").attr("transform", `translate(0,${viewY.range()[0] + axisOffset})`).call(xAxis);
    xAxisG.selectAll("text").attr("font-size", 11).attr("fill", colorScheme === "dark" ? "#d5d7e0" : "#495057").attr("font-family", fontFamily).attr("text-anchor", "middle");
    const tickTexts = xAxisG.selectAll("text").nodes();
    if (tickTexts.length > 14) { const skip = Math.ceil(tickTexts.length / 14); tickTexts.forEach((n, i) => { if (i !== 0 && i !== tickTexts.length - 1 && i % skip !== 0) n.style.display = "none"; }); }
    mainSvg.append("g").attr("transform", `translate(${margin.left},0)`).call(yAxis).selectAll("text").attr("font-size", 11).attr("fill", colorScheme === "dark" ? "#d5d7e0" : "#495057").attr("font-family", fontFamily);
    mainSvg.selectAll(".domain,.tick line").attr("stroke", "none");
    if (niceDomain[0] < 0) { mainSvg.append("line").attr("x1", margin.left).attr("x2", width - margin.right).attr("y1", viewY(0)).attr("y2", viewY(0)).attr("stroke", colorScheme === "dark" ? "#555" : "#999").attr("stroke-width", 1).attr("stroke-dasharray", "4,4"); }
    const yLabelEl = mainSvg.append("text").attr("transform", "rotate(-90)").attr("x", -(mainH / 2)).attr("y", 18).attr("text-anchor", "middle").attr("fill", colorScheme === "dark" ? "#d5d7e0" : "#495057").attr("font-size", 12);
    yLabelEl.append("tspan").text(`Power (${unitLabel}`);
    yLabelEl.append("tspan").attr("baseline-shift", "sub").attr("font-size", 9).attr("font-style", "italic").text("th");
    yLabelEl.append("tspan").text(")");

    // Clip path + lines
    const clipId = secureUniqueId("clip");
    mainSvg.append("clipPath").attr("id", clipId).append("rect").attr("x", margin.left).attr("y", margin.top).attr("width", width - margin.left - margin.right).attr("height", viewY.range()[0] - margin.top);
    series.forEach((s) => {
      if (hidden.has(s.key)) return;
      const l = d3.line()
        .defined((j) => { if (j >= s.values.length || s.values[j] == null) return false; const sc = scaleValue(s.values[j]); return sc != null && Number.isFinite(sc); })
        .x((j) => viewX(times[j])).y((j) => viewY(scaleValue(s.values[j])));
      const visibleIdxs = idxs.filter((j) => j < times.length);
      const pathData = l(visibleIdxs);
      const dataLayer = mainSvg.append("g").attr("class", "data-layer").attr("clip-path", `url(#${clipId})`);
      if (pathData) { dataLayer.append("path").attr("d", pathData).attr("fill", "none").attr("stroke", s.color).attr("stroke-width", 1.4).attr("opacity", 0.95); }
    });

    // Tooltip setup
    if (!tooltipRef.current) {
      const tt = document.createElement("div");
      tt.style.cssText = `position:absolute;pointer-events:none;background:${colorScheme === "dark" ? "#222" : "#fff"};border:1px solid ${colorScheme === "dark" ? "#555" : "#666"};padding:6px 8px;font-size:12px;font-family:${fontFamily};border-radius:4px;box-shadow:0 2px 6px rgba(0,0,0,.25);display:none;z-index:40;`;
      tooltipRef.current = tt; root.appendChild(tt); root.style.position = "relative";
    }
    const tooltip = tooltipRef.current;
    const plotWidth = width - margin.left - margin.right;

    const hoverRect = mainSvg.append("rect").attr("x", margin.left).attr("y", margin.top).attr("width", plotWidth).attr("height", viewY.range()[0] - margin.top).attr("fill", "transparent").style("cursor", panMode ? (xDomain ? "grab" : "not-allowed") : "crosshair");
    let selection = null; let selectionRect = null; let panRef = null;

    hoverRect.on("mousedown", (ev) => {
      ev.preventDefault(); const pt = d3.pointer(ev); if (ev.button !== 0) return;
      if (panMode) {
        let domainForPan = xDomain;
        if (!domainForPan) { const fullSpan = fullExtent[1] - fullExtent[0]; const newSpan = fullSpan * 0.5; const mid = new Date((fullExtent[0].getTime() + fullExtent[1].getTime()) / 2); const start = new Date(mid.getTime() - newSpan / 2); const end = new Date(mid.getTime() + newSpan / 2); setXDomain([start, end]); domainForPan = [start, end]; }
        panRef = { startX: pt[0], domain: [...domainForPan] }; hoverRect.style("cursor", "grabbing");
        const panMoveHandler = (e) => { const ptMove = d3.pointer(e, mainRef.current); let dx = ptMove[0] - panRef.startX; const sp = panRef.domain[1] - panRef.domain[0]; if (sp <= 0) return; const mpp = sp / plotWidth; let shiftMs = -dx * mpp; const minS = fullExtent[0] - panRef.domain[0]; const maxS = fullExtent[1] - panRef.domain[1]; if (shiftMs < minS) shiftMs = minS; if (shiftMs > maxS) shiftMs = maxS; setXDomain([new Date(panRef.domain[0].getTime() + shiftMs), new Date(panRef.domain[1].getTime() + shiftMs)]); };
        window.addEventListener("mousemove", panMoveHandler);
        window.addEventListener("mouseup", function panUpHandler() { window.removeEventListener("mousemove", panMoveHandler); window.removeEventListener("mouseup", panUpHandler); hoverRect.style("cursor", panMode ? (xDomain ? "grab" : "not-allowed") : "crosshair"); });
      } else {
        selection = { startX: pt[0], endX: pt[0] };
        selectionRect = mainSvg.append("rect").attr("class", "zoom-box").attr("x", pt[0]).attr("y", margin.top).attr("width", 0).attr("height", viewY.range()[0] - margin.top).attr("fill", colorScheme === "dark" ? "rgba(80,160,255,0.20)" : "rgba(0,123,255,0.15)").attr("stroke", colorScheme === "dark" ? "#4da3ff" : "#007bff").attr("stroke-dasharray", "3,2");
        hoverRect.style("cursor", "col-resize");
        window.addEventListener("mouseup", onPointerUp, { once: true });
      }
    });

    const onPointerUp = () => {
      if (selection) {
        const x0 = Math.max(margin.left, Math.min(selection.startX, selection.endX));
        const x1 = Math.min(margin.left + plotWidth, Math.max(selection.startX, selection.endX));
        if (x1 - x0 > 5) { const d0 = viewX.invert(x0); const d1 = viewX.invert(x1); const idx0 = d3.bisector((d) => d).left(times, d0); const idx1 = d3.bisector((d) => d).right(times, d1) - 1; if (idx1 - idx0 + 1 >= 3 && d1 - d0 > stepMs * 2) setXDomain([d0, d1]); }
        selectionRect && selectionRect.remove();
      }
      selection = null; selectionRect = null; hoverRect.style("cursor", panMode ? (xDomain ? "grab" : "not-allowed") : "crosshair");
    };

    const vLine = mainSvg.append("line").attr("stroke", colorScheme === "dark" ? "#888" : "#666").attr("stroke-dasharray", "3,3").style("display", "none");

    const onSelectionMove = (ev) => {
      if (!selection) return; const pt = d3.pointer(ev); selection.endX = pt[0];
      const x0 = Math.max(margin.left, Math.min(selection.startX, selection.endX));
      const x1 = Math.min(margin.left + plotWidth, Math.max(selection.startX, selection.endX));
      selectionRect.attr("x", x0).attr("width", Math.max(0, x1 - x0));
    };

    const onTooltipMove = (ev) => {
      if (selection || panRef) return;
      const [mx] = d3.pointer(ev); const date = viewX.invert(mx); if (!date) return;
      const idx = d3.bisector((d) => d).center(times, date);
      if (idx < 0 || idx >= times.length) return;
      if (times[idx] < originalDomainStart || times[idx] > originalDomainEnd) { tooltip.style.display = "none"; vLine.style("display", "none"); return; }
      vLine.attr("x1", viewX(times[idx])).attr("x2", viewX(times[idx])).attr("y1", margin.top).attr("y2", viewY.range()[0]).style("display", "block");
      let html = `<strong>${times[idx].toLocaleString()}</strong><br/>`;
      series.forEach((s) => { if (hidden.has(s.key)) return; const v = scaleValue(s.values[idx]); if (v == null || isNaN(v)) return; html += `<span style="color:${s.color}">●</span> ${s.label}: <b>${formatEnUsValue(v)}</b> ${unitLabelHtml}<br/>`; });
      tooltip.innerHTML = html; tooltip.style.left = viewX(times[idx]) + 12 + "px"; tooltip.style.top = margin.top + 8 + "px"; tooltip.style.display = "block";
      d3.selectAll(".dot").remove();
      const dataLayerSel = d3.select(mainRef.current).select(".data-layer");
      series.forEach((s) => { if (hidden.has(s.key)) return; const v = scaleValue(s.values[idx]); if (v == null || isNaN(v)) return; dataLayerSel.append("circle").attr("class", `dot dot-${s.key}`).attr("cx", viewX(times[idx])).attr("cy", viewY(v)).attr("r", 2).attr("fill", "#eb7857").attr("stroke", colorScheme === "dark" ? "#fff" : "#222").attr("stroke-width", 1.2).attr("opacity", 0.95); });
    };

    const onTooltipLeave = () => { if (selection || panRef) return; tooltip.style.display = "none"; vLine.style("display", "none"); };

    hoverRect.on("mousemove.zoom", panMode ? null : onSelectionMove).on("mousemove.tooltip", onTooltipMove).on("mouseleave", onTooltipLeave);
  };

  useEffect(() => { render(); }, [series, colorScheme, hidden, times, xDomain, panMode, powerUnit, unitHelpers]);
  useEffect(() => { const onR = () => render(); window.addEventListener("resize", onR); return () => window.removeEventListener("resize", onR); }, [results, colorScheme, hidden, unitHelpers]);

  return (
    <div ref={wrapperRef} className={`rs-wrapper ${colorScheme === "dark" ? "dark" : "light"}`}>
      <div className="rs-toolbar">
        <button className="rs-btn" title={inModalInstance ? t("common.closeFullscreen") : t("common.openFullscreen")} onClick={() => { if (inModalInstance) { handleCloseModal(); } else { setFullscreenModal(true); } }}>
          <span role="img" aria-hidden="true" className="rs-pan-icon">{inModalInstance ? "🗗" : "⛶"}</span>
        </button>
        <button className={`rs-btn rs-btn-pan ${panMode ? "is-active" : ""}`} title={panMode ? "Pan enabled (click to disable)" : "Pan disabled (click to enable)"} aria-pressed={panMode} onClick={() => setPanMode((p) => !p)}>
          <span role="img" aria-hidden="true" className="rs-pan-icon">{panMode ? "🖐️" : "↔"}</span>
        </button>
        <button className="rs-btn" title={t("common.zoomIn")} onClick={() => {
          if (!times.length) return; const full = [times[0], times[times.length - 1]]; const minPoints = 3;
          if (!xDomain) { const span = full[1] - full[0]; const newSpan = span * 0.5; const mid = new Date((full[0].getTime() + full[1].getTime()) / 2); const d0 = new Date(mid.getTime() - newSpan / 2); const d1 = new Date(mid.getTime() + newSpan / 2); const idx0 = d3.bisector((d) => d).left(times, d0); const idx1 = d3.bisector((d) => d).right(times, d1) - 1; if (idx1 - idx0 + 1 >= minPoints) setXDomain([d0, d1]); return; }
          const old = xDomain; const span = old[1] - old[0]; const mid = new Date((old[0].getTime() + old[1].getTime()) / 2); let newSpan = Math.max(span * 0.5, stepMs * 4); const d0 = new Date(mid.getTime() - newSpan / 2); const d1 = new Date(mid.getTime() + newSpan / 2); const idx0 = d3.bisector((d) => d).left(times, d0); const idx1 = d3.bisector((d) => d).right(times, d1) - 1; if (idx1 - idx0 + 1 >= minPoints) setXDomain([d0, d1]);
        }}>＋</button>
        <button className="rs-btn" title={t("common.zoomOut")} onClick={() => {
          if (!times.length || !xDomain) return; const full = [times[0], times[times.length - 1]]; const old = xDomain; const span = old[1] - old[0]; const mid = new Date((old[0].getTime() + old[1].getTime()) / 2); let newSpan = span / 0.7; if (newSpan >= full[1] - full[0]) { setXDomain(null); return; } setXDomain([new Date(mid.getTime() - newSpan / 2), new Date(mid.getTime() + newSpan / 2)]);
        }}>－</button>
        <button className="rs-btn" title={t("common.resetZoom")} onClick={() => setXDomain(null)}>Reset</button>
        <select className="rs-btn rs-download-select" value={powerUnit} title={t("timeSeries.unitToggle")} onChange={(e) => setPowerUnit(e.target.value)} style={{ width: 90 }}>
          <option value="W"><span>W<sub><i>th</i></sub></span></option>
          <option value="kW"><span>kW<sub><i>th</i></sub></span></option>
        </select>
        <select className="rs-btn rs-download-select" title={t("common.download")} value="" onChange={(e) => {
          const value = e.target.value;
          if (value === "png") {
            const svgEl = mainRef.current; if (!svgEl) return;
            const serializer = new XMLSerializer(); const cloned = svgEl.cloneNode(true);
            const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
            bg.setAttribute("x", "0"); bg.setAttribute("y", "0");
            bg.setAttribute("width", svgEl.clientWidth || svgEl.getAttribute("width") || 1200);
            bg.setAttribute("height", svgEl.clientHeight || svgEl.getAttribute("height") || 600);
            bg.setAttribute("fill", colorScheme === "dark" ? "#111" : "#ffffff");
            cloned.insertBefore(bg, cloned.firstChild);
            const img = new Image(); const canvas = document.createElement("canvas");
            const w = svgEl.clientWidth || parseInt(svgEl.getAttribute("width"), 10) || 1200;
            const h = svgEl.clientHeight || parseInt(svgEl.getAttribute("height"), 10) || 600;
            canvas.width = w; canvas.height = h;
            img.onload = () => { const ctx = canvas.getContext("2d"); ctx.drawImage(img, 0, 0); canvas.toBlob((blob) => { if (!blob) return; const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `thermal-series-${new Date().toISOString().replace(/[:.]/g, "-")}.png`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); }); };
            img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(serializer.serializeToString(cloned));
          } else if (value === "csv") {
            try {
              if (!times.length || !series.length) return;
              const { unitLabel, scaleValue } = unitHelpers;
              const csvEscape = (val) => { const s = String(val); return /[,"\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
              const rows = [["DateTime", ...series.map((s) => `${s.label} (${unitLabel})`)].map(csvEscape).join(",")];
              for (let i = 0; i < times.length; i++) { const dateStr = moment(times[i]).format("YYYY-MM-DD HH:mm:ss"); rows.push([dateStr, ...series.map((s) => { const v = scaleValue(s.values[i]); return v != null ? v : ""; })].join(",")); }
              const BOM = "\uFEFF"; const blob = new Blob([BOM + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.download = `thermal-series-${new Date().toISOString().replace(/[:.]/g, "-")}.csv`; a.href = url; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
            } catch (err) { /* silent fail */ }
          }
          e.target.selectedIndex = 0;
        }}>
          <option value="" disabled hidden>{t("common.download") || "Download"}</option>
          <option value="png">PNG</option>
          <option value="csv">CSV</option>
        </select>
      </div>
      <div className="rs-legend" />
      <svg ref={mainRef} className="indicator-downloadable rs-chart" />
      <FullscreenModal open={fullscreenModal && !inModalInstance} onClose={handleCloseModal} title={t("result.timeSeries.thermal")}>
        <ResultSeriesThermal results={results} onFullscreen={handleCloseModal} inModalInstance={true} kpiProjectSettings={kpiProjectSettings} />
      </FullscreenModal>
    </div>
  );
};

export default ResultSeriesThermal;
